import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { RHFAutocomplete, RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import UniqueTextField from 'src/components/unique-text-field/UniqueTextField';
import ListboxComponent from 'src/components/virtualized-autocomplete/autocomplete-virtualized';
import Iconify from 'src/components/iconify';
import { Hidden, InputAdornment, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

export default function EmpVisaNewEditForm({
  visaNmArr,
  visaTypArr,
  citiesData,
  countryArr,
  switchState,
  onEmptyVisa,
  onDuplicateStatusChange,
  currentEmp,
  duplicateFields = {},
}) {
  const { control, setValue, watch, resetField, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'empVisa',
  });
  const handleAdd = () => {
    append({
      _uid: `visa_${Date.now()}_${fields.length}`,
      visaSrno: 0,
      visaNo: '',
      visaTyp: null,
      visaNm: null,
      expiryDt: null,
      issueDt: null,
      placeOfissue: null,
      forCntry: null,
    });
  };

  const handleRemove = (index) => {
    const item = fields[index];
    const rowKey = item?._uid || item?.id || `idx_${index}`;
    if (onDuplicateStatusChange) {
      onDuplicateStatusChange(`visa-${rowKey}`)(false);
      if (item?.id) {
        onDuplicateStatusChange(`visa-${item.id}`)(false);
      }
      onDuplicateStatusChange(`visa-${index}`)(false);
    }
    remove(index);
  };

  // Only call onEmptyVisa() after mount and update, not on initial render to prevent inconsistency
  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current && fields.length === 0) {
      // Delay to let React form state settle before unmount
      setTimeout(() => {
        onEmptyVisa?.();
      }, 0);
    }
    hasMounted.current = true;
  }, [fields.length, onEmptyVisa]);

  return (
    <>
      <Box
        mt={4}
        rowGap={3}
        columnGap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
        }}
        mb={3}
      >
        {/* <Typography variant="subtitle2" sx={{ mb: '10px' }}>
          Visa Details:
        </Typography> */}
        <Box>
          <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
            Visa Details:
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-end', md: 'center' }}
          justifyContent="flex-end"
        >
          <Button
            size="small"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleAdd}
            sx={{ flexShrink: 0, minWidth: '140px', justifyContent: 'flex-start' }}
          >
            Add Visa
          </Button>
        </Stack>
      </Box>
      <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />} spacing={2}>
        {fields.map((item, index) => (
          <Stack key={item.id} spacing={1.5}>
            <Typography variant="subtitle2" sx={{ color: 'primary', mb: 1 }}>
              Visa {index + 1}:
            </Typography>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <RHFTextField name={`empVisa[${index}].visaSrno`} />
            </Box>
            <Box
              rowGap={1.5}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Controller
                name={`empVisa[${index}].visaNo`}
                control={control}
                rules={{ required: 'Visa Number is required' }}
                render={({ field, fieldState }) => {
                  const rowKey = item._uid || item.id || `idx_${index}`;
                  const isDuplicate = Boolean(
                    duplicateFields[`visa-${rowKey}`] ||
                    duplicateFields[`visa-${item.id}`] ||
                    duplicateFields[`visa-${index}`]
                  );
                  const customField = {
                    ...field,
                    onChange: (e) => {
                      let value = e.target.value.toUpperCase();
                      value = value.replace(/[^A-Z0-9]/g, '');
                      field.onChange(value);
                    },
                  };
                  return (
                    <UniqueTextField
                      field={customField}
                      error={fieldState.error}
                      isDuplicate={isDuplicate}
                      label={
                        <div>
                          Visa Number<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                      fieldName="visaNumber"
                      apiEndpoint="/rest/employee/check-duplicate-visa-number"
                      entityId={item.visaSrno > 0 ? item.visaSrno : null}
                      onDuplicateStatusChange={
                        onDuplicateStatusChange
                          ? onDuplicateStatusChange(`visa-${rowKey}`)
                          : undefined
                      }
                      duplicateMessage="This Visa Number already exists"
                      validationRegex={/^[A-Z0-9]{8,10}$/}
                      fullWidth
                    />
                  );
                }}
              />
              <Stack spacing={1}>
                <RHFAutocomplete
                  name={`empVisa[${index}].visaNm`}
                  placeholder="Visa Name"
                  label="Visa Name"
                  options={visaNmArr}
                  showPlusIcon
                  getOptionLabel={(option) => option.masterName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.masterId}>
                      {option.masterName}
                    </li>
                  )}
                />
              </Stack>
              <Stack spacing={1}>
                <RHFAutocomplete
                  name={`empVisa[${index}].visaTyp`}
                  placeholder="Visa Type"
                  label={
                    <div>
                      Visa Type<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={visaTypArr}
                  showPlusIcon
                  getOptionLabel={(option) => option.masterName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.masterId}>
                      {option.masterName}
                    </li>
                  )}
                />
              </Stack>
              <Stack spacing={1}>
                <RHFAutocomplete
                  name={`empVisa[${index}].forCntry`}
                  placeholder="Visa For"
                  label={
                    <div>
                      Visa For<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={countryArr}
                  getOptionLabel={(option) => option.countryName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.countriesId}>
                      {option.countryName}
                    </li>
                  )}
                />
              </Stack>
              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empVisa[${index}].placeOfissue`}
                  placeholder="Select Place Of Issue"
                  label="Place Of Issue"
                  ListboxComponent={ListboxComponent}
                  options={citiesData}
                  getOptionLabel={(option) => option.cityName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.cityId}>
                      {option.cityName}
                    </li>
                  )}
                  sx={{ mb: 1.5 }}
                />
              </Stack>

              <Controller
                name={`empVisa[${index}].issueDt`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Date of Issue"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      trigger(`empVisa[${index}].issueDt`);
                    }}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name={`empVisa[${index}].expiryDt`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Date of Expiry"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-end', md: 'center' }}
              justifyContent="flex-end"
            >
              <Button
                size="small"
                color="error"
                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </>
  );
}
EmpVisaNewEditForm.propTypes = {
  visaNmArr: PropTypes.array,
  visaTypArr: PropTypes.array,
  citiesData: PropTypes.array,
  countryArr: PropTypes.array,
  switchState: PropTypes.object,
  onEmptyVisa: PropTypes.func,
  onDuplicateStatusChange: PropTypes.func,
  currentEmp: PropTypes.object,
  duplicateFields: PropTypes.object,
};
